import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useRedux from "src/redux/useRedux";
import { createVoucher, detailVoucher, updateVoucher } from "src/redux/voucher";

const defaultPayload = {
  value: "",
  purchasePrice: "",
  expiryDay: "",
  name: "",
  code: "",
  description: "",
  voucherType: "",
  valueText: "",
  timeType: "",
  startDate: "",
  endDate: "",
  maxLimit: "",
};

export const useVoucherForm = (id?: any) => {
    const {thunkDispatch} = useRedux();
    const navigate = useNavigate();

    const [payload, setPayload] = useState<Record<string, string>>(defaultPayload);
    const [text, setText] =  useState<string>("");
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState<boolean>(false);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!payload.name) newErrors.name = "Nama voucher wajib diisi";
    else if (payload.name.length > 128) newErrors.name = "Maksimal 12 karakter";

    if (!payload.description) newErrors.description = "Deskripsi wajib diisi";

    if (!payload.code) newErrors.code = "Kode voucher wajib diisi";
    else if (payload.code.length > 45) newErrors.code = "Maksimal 45 karakter";

    // if (!payload.purchasePrice) newErrors.purchasePrice = "Harga voucher wajib diisi";

    if (!payload.valueText) newErrors.valueText = "Tipe nilai voucher wajib diisi";
    if (!payload.value) newErrors.value = "Nilai voucher wajib diisi";
    else if (
      payload.valueText === "PERCENT" && Number(payload.value) >= 100
    ) {
      newErrors.value = "Presentase nilai voucher tidak boleh melebihi 99";
    }

    if (!payload.voucherType) newErrors.voucherType = "Tipe voucher wajib diisi";
    if (!payload.maxLimit || Number(payload.maxLimit) <= 0) {
      newErrors.maxLimit = "Limit penggunaan wajib diisi dan lebih dari 0";
    }

    if (!payload.timeType) newErrors.timeType = "Tipe waktu voucher wajib diisi";
    if (payload.timeType === "expire_date" && !parseInt(payload.expiryDay)) {
      newErrors.expiryDay = "Masa berlaku wajib diisi";
    }
    if (payload.timeType === "period") {
      if (!payload.startDate) newErrors.startDate = "Tanggal mulai wajib diisi";
      if (!payload.endDate) newErrors.endDate = "Tanggal berakhir wajib diisi";
      if (
        payload.startDate &&
        payload.endDate &&
        new Date(payload.endDate) < new Date(payload.startDate)
      ) {
        newErrors.endDate = "Tanggal berakhir tidak boleh sebelum tanggal mulai";
      }
    }

    if (!text) newErrors.text = "Cara penggunaan wajib diisi";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const initialize = async () => {
    setLoading(true);
        try {
          const res = await thunkDispatch(detailVoucher(id)).unwrap();
          console.log("respon", res)
          if (res && res?.status === "success") {
            const data = res?.data?.data;
            setPayload({
              value: String(data.value),
              purchasePrice: String(data.purchasePrice),
              expiryDay: String(data.expiryDay),
              name: data.name,
              code: data.code,
              description: data.description,
              voucherType: data.voucherType,
              valueText: data.valueText,
              timeType: data.timeType,
              startDate: data.startDate ? new Date(data.startDate).toISOString() : "",
              endDate: data.endDate ? new Date(data.endDate).toISOString() : "",
              maxLimit: data.maxLimit,
            });
            setText(data.howToUse.content);
          }
      } catch (error) {
          console.error("Error initializing form", error);
          toast.error("Gagal memuat data voucher!");
      } finally {
          setLoading(false);
      }
    };

    const handleSubmitUpdate = async () => {
    if (!validate()) return;
    setLoading(true);

    const data = {
      id: id || "",
      value: payload.value,
      purchasePrice: payload.purchasePrice,
      expiryDay: payload.expiryDay,
      name: payload.name,
      code: payload.code,
      description: payload.description,
      voucherType: payload.voucherType,
      valueText: payload.valueText,
      timeType: payload.timeType,
      startDate: payload.startDate,
      endDate: payload.endDate,
      maxLimit: payload.maxLimit,
      howToUse: text,
      ...payload,
    };

    try {
      const res = await thunkDispatch(updateVoucher(data)).unwrap();
      if (res?.status === "success") {
        toast.success("Berhasil memperbarui data voucher");
          setLoading(false);
          setTimeout(() => {
            navigate("/products/vouchers");
          }, 1000);
      } else {
        toast.error("Gagal memperbarui data voucher!");
      }
    } catch (error) {
      console.log("Error on submit update voucher", error)
      toast.error("Terjadi kesalahan saat memperbarui data");
    } finally {
      setLoading(false);
    }
  };

    const handleSubmitCreate = async () => {
    if (!validate()) return;
    setLoading(true);

    const data = {
      value: payload.value,
      purchasePrice: payload.purchasePrice,
      expiryDay: payload.expiryDay,
      name: payload.name,
      code: payload.code,
      description: payload.description,
      voucherType: payload.voucherType,
      valueText: payload.valueText,
      timeType: payload.timeType,
      startDate: payload.startDate,
      endDate: payload.endDate,
      maxLimit: payload.maxLimit,
      howToUse: text,
      ...payload,
    };

    try {
      const res = await thunkDispatch(createVoucher(data)).unwrap();
      if (res?.status === "success") {
        toast.success("Berhasil menambahkan data voucher");
          setLoading(false);
          setTimeout(() => {
            navigate("/products/vouchers");
          }, 1000);
      } else {
        toast.error("Gagal menambahkan data voucher!");
      }
    } catch (error) {
      console.log("Error on submit create voucher", error)
      toast.error("Terjadi kesalahan saat menambahkan data");
    } finally {
      setLoading(false);
    }
  };

  return {
    payload,
    setPayload,
    text,
    setText,
    errors,
    handleSubmitUpdate,
    handleSubmitCreate,
    loading,
    initialize,
  };
};
